import {Button, Icon} from '@startlibs/components'
import {useToggle} from '@startlibs/core'
import {FormContext, useFormValue} from '@startlibs/form'
import {getColor} from '@startlibs/utils'
import React, {useContext, useEffect, useState} from 'react'
import styled, {css} from 'styled-components'
import {FormattedMessage, useIntl} from 'react-intl'
import {Card, SectionHeading} from '../../components/PageLayout'
import {setNotification} from '../../components/Notifications'
import {
  EXPERT_TIME_ERROR_RANGE,
  EXPERT_TIME_ERROR_REQUIRED,
  MAX_EXPERT_TIME_HOURS,
  saveExpertTime,
  splitMinutes,
  toTotalMinutes,
  validateExpertTime
} from './expertTimeUtils'

const ERROR_COLOR = '#c3282d'

const pad = (v) => (v ?? '') === '' ? '' : String(v).padStart(2, '0')
const padFields = ({hours, minutes}) => ({hours: pad(hours), minutes: pad(minutes)})

const HeadingWrapper = styled(SectionHeading)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  h3 {
    ${props => props.hasError && css`color: ${ERROR_COLOR};`}
  }
  .notice {
    color: ${getColor('gray150')};
    font-size: 12px;
  }
`

const TimeCard = styled(Card)`
  position: relative;
  padding: 1.5rem 2.5rem;
  ${props => props.hasError && css`border: 1px solid ${ERROR_COLOR};`}
`

const EditorLayout = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 1.5rem 4rem;
  padding: 1rem 0;
  .help {
    flex: 1;
    min-width: 16rem;
    margin: 0;
    color: ${getColor('gray150')};
  }
`

const InputsBox = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 1.75rem 1.5rem 1.25rem;
  background: ${getColor('gray240')};
  border: 1px solid ${props => props.hasError ? ERROR_COLOR : getColor('gray210')};
  border-radius: 6px;
  .separator {
    line-height: 3.5rem;
    margin: 0 0.75rem;
    color: ${getColor('gray120')};
  }
`

const TimeFieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  label {
    margin: 0.375rem 0 0;
    color: ${getColor('gray150')};
    font-weight: normal;
    font-size: 12px;
  }
`

const TimeBox = styled.div`
  display: flex;
  align-items: center;
  height: 3.5rem;
  width: 6rem;
  padding: 0 0.5rem 0 1.125rem;
  background: white;
  border: 1px solid ${getColor('gray210')};
  border-radius: 8px;
  input {
    width: 100%;
    min-width: 0;
    border: 0;
    outline: 0;
    background: transparent;
    font-size: 16px;
    color: ${getColor('gray60')};
    &::placeholder {
      color: ${getColor('gray180')};
    }
  }
  &:focus-within {
    border-color: ${getColor('main')};
  }
`

const Arrows = styled.div`
  display: flex;
  flex-direction: column;
  button {
    border: 0;
    background: transparent;
    padding: 0.125rem 0.25rem;
    color: ${getColor('main')};
    cursor: pointer;
    font-size: 11px;
    line-height: 1;
    &:disabled {
      color: ${getColor('gray180')};
      cursor: default;
    }
  }
`

const DisplayLayout = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  .time {
    display: flex;
    align-items: flex-start;
  }
  .value {
    display: flex;
    flex-direction: column;
    align-items: center;
    strong {
      font-size: 26px;
      font-weight: normal;
      color: ${getColor('gray60')};
      line-height: 1.2;
    }
    span {
      color: ${getColor('gray150')};
      font-size: 12px;
    }
  }
  .separator {
    font-size: 18px;
    line-height: 31px;
    margin: 0 1.5rem;
    color: ${getColor('gray120')};
  }
`

const ErrorText = styled.p`
  color: ${ERROR_COLOR};
  margin: 0.5rem 0 0;
`

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  ${Button} {
    margin-left: 1rem;
  }
`

// `max` bounds the arrows; `typingMax` bounds what can be typed (values above `max` are left for validation).
const TimeField = ({value, onChange, max, typingMax = Infinity, maxLength, label, disabled}) => {
  const step = (delta) => onChange(pad(Math.min(Math.max((Number(value) || 0) + delta, 0), max)))

  const onInputChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '')
    onChange(digits === '' ? '' : String(Math.min(Number(digits), typingMax)))
  }

  const onKeyDown = (e) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      step(1)
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      step(-1)
    }
  }

  return <TimeFieldWrapper>
    <TimeBox>
      <input
        type="text"
        inputMode="numeric"
        placeholder="00"
        aria-label={label}
        value={value}
        maxLength={maxLength}
        disabled={disabled}
        onChange={onInputChange}
        onKeyDown={onKeyDown}
        onBlur={() => onChange(pad(value))}
      />
      {!disabled && <Arrows>
        <button type="button" tabIndex={-1} disabled={Number(value) >= max} onClick={() => step(1)}><Icon icon="arrow-up"/></button>
        <button type="button" tabIndex={-1} disabled={!Number(value)} onClick={() => step(-1)}><Icon icon="arrow-down"/></button>
      </Arrows>}
    </TimeBox>
    <label>{label}</label>
  </TimeFieldWrapper>
}

// Controlled by the total in minutes (null when never entered).
export const ExpertTimeInput = ({value, onChange, disabled, hasError}) => {
  const intl = useIntl()
  const [fields, setFields] = useState(() => padFields(splitMinutes(value)))

  useEffect(() => {
    if (toTotalMinutes(fields.hours, fields.minutes) !== (value ?? null)) {
      setFields(padFields(splitMinutes(value)))
    }
  }, [value])

  const update = (key) => (v) => {
    const next = {...fields, [key]: v}
    setFields(next)
    onChange(toTotalMinutes(next.hours, next.minutes))
  }

  return <InputsBox hasError={hasError}>
    <TimeField
      value={fields.hours}
      onChange={update('hours')}
      max={MAX_EXPERT_TIME_HOURS}
      maxLength={3}
      disabled={disabled}
      label={intl.formatMessage({defaultMessage: 'hours', description: 'Expert time spent hours label'})}
    />
    <span className="separator">:</span>
    <TimeField
      value={fields.minutes}
      onChange={update('minutes')}
      max={59}
      typingMax={59}
      maxLength={2}
      disabled={disabled}
      label={intl.formatMessage({defaultMessage: 'minutes', description: 'Expert time spent minutes label'})}
    />
  </InputsBox>
}

export const ExpertTimeHeading = ({required, hasError, showNotice, className, children}) => <HeadingWrapper hasError={hasError} className={className}>
  <h3><FormattedMessage
    defaultMessage="Total time spent on case"
    description="Expert time spent section title"
  />{required && <span css={`color: ${ERROR_COLOR};`}>*</span>}</h3>
  {showNotice && <span className="notice"><FormattedMessage
    defaultMessage="Only you and administrators can see this"
    description="Expert time spent visibility notice"
  /></span>}
  {children}
</HeadingWrapper>

// Returns the validation message for the given total, or undefined when valid.
export const getExpertTimeErrorMessage = (intl, minutes, required) => {
  const error = validateExpertTime(minutes, required)
  if (error === EXPERT_TIME_ERROR_REQUIRED) {
    return intl.formatMessage({
      defaultMessage: 'You must enter time spent on case.',
      description: 'Expert time spent required validation error'
    })
  }
  if (error === EXPERT_TIME_ERROR_RANGE) {
    return intl.formatMessage({
      defaultMessage: 'Total time spent on case must be between 0 and {max} hours',
      description: 'Expert time spent out of range validation error'
    }, {max: `${MAX_EXPERT_TIME_HOURS}:59`})
  }
}

export const ExpertTimeEditor = ({value, onChange, disabled, hasError}) => <EditorLayout>
  <ExpertTimeInput value={value} onChange={onChange} disabled={disabled} hasError={hasError}/>
  <p className="help"><FormattedMessage
    defaultMessage="Please enter the time you dedicated to this case. This information will not be visible in the final case report."
    description="Expert time spent help text"
  /></p>
</EditorLayout>

export const ExpertTimeDisplay = ({minutes, children}) => {
  const intl = useIntl()
  const {hours, minutes: mins} = splitMinutes(minutes)
  return <DisplayLayout>
    <div className="time">
      <div className="value">
        <strong>{pad(hours) || '--'}</strong>
        <span>{intl.formatMessage({defaultMessage: 'hours', description: 'Expert time spent hours label'})}</span>
      </div>
      <span className="separator">:</span>
      <div className="value">
        <strong>{pad(mins) || '--'}</strong>
        <span>{intl.formatMessage({defaultMessage: 'minutes', description: 'Expert time spent minutes label'})}</span>
      </div>
    </div>
    {children}
  </DisplayLayout>
}

// Inside a WithForm: the value lives on the form path and is sent with the review payload.
export const ExpertTimeFormCard = ({path, readOnly, hasError}) => {
  const form = useContext(FormContext)
  const [value] = useFormValue(path)

  const onChange = (minutes) => {
    form.setValue(path, minutes)
    form.removeErrorPath(path)
  }

  return <TimeCard hasError={hasError}>
    {readOnly
      ? <ExpertTimeDisplay minutes={value ?? null}/>
      : <ExpertTimeEditor value={value ?? null} onChange={onChange} hasError={hasError}/>
    }
  </TimeCard>
}

// Standalone view with an Edit button; saves through PUT /caseExpertTime unless `onEdit` handles editing elsewhere.
export const ExpertTimeCard = ({requestId, entry, required, onSaved, onEdit, canEdit = true, warnMissing = true}) => {
  const intl = useIntl()
  const editing = useToggle()
  const saving = useToggle()
  const [draft, setDraft] = useState(entry?.minutes ?? null)
  const [error, setError] = useState()

  const startEditing = () => {
    setDraft(entry?.minutes ?? null)
    setError(undefined)
    editing.open()
  }

  const changeDraft = (minutes) => {
    setDraft(minutes)
    setError(undefined)
  }

  const save = () => {
    const message = getExpertTimeErrorMessage(intl, draft, required)
    if (message) return setError(message)
    if (draft === null || draft === entry?.minutes) return editing.close()
    return saving.wait(saveExpertTime(requestId, draft)
      .then((updated) => {
        onSaved({...updated, minutes: updated?.minutes ?? draft})
        editing.close()
      })
      .catch(() => setNotification({
        type: 'alert',
        msg: () => <FormattedMessage
          defaultMessage="Total time spent on case could not be saved."
          description="Expert time spent save error"
        />
      }))
    )
  }

  const isEditable = canEdit && entry?.canEdit
  const missingRequired = warnMissing && isEditable && required && (entry?.minutes ?? null) === null

  return <TimeCard noPrint hasError={!!error || missingRequired}>
    {editing.isOpen
      ? <>
        <ExpertTimeEditor value={draft} onChange={changeDraft} hasError={!!error}/>
        {error && <ErrorText>{error}</ErrorText>}
        <Actions>
          <Button small onClick={editing.close}><FormattedMessage
            defaultMessage="Cancel"
            description="Cancel Button"
          /></Button>
          <Button small highlight isLoading={saving.isOpen} onClick={save}><FormattedMessage
            defaultMessage="Save"
            description="Expert time spent save button"
          /></Button>
        </Actions>
      </>
      : <ExpertTimeDisplay minutes={entry?.minutes}>
        {isEditable && <Button small onClick={onEdit || startEditing}><FormattedMessage
          defaultMessage="Edit"
          description="Expert time spent edit button"
        /></Button>}
      </ExpertTimeDisplay>
    }
    {!editing.isOpen && missingRequired && <ErrorText>{getExpertTimeErrorMessage(intl, null, true)}</ErrorText>}
  </TimeCard>
}
